import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { ModalService } from '../services/modal.service';
import {
  ErrorModalComponent,
  ErrorModalData,
} from '../components/error-modal/error-modal.component';

function normalizeMessage(message: string | string[] | undefined): string {
  if (message == null) return '';
  if (Array.isArray(message)) return message.length > 0 ? message.join('\n') : '';
  return String(message);
}

function buildErrorPayload(error: HttpErrorResponse): ErrorModalData {
  const body = error.error && typeof error.error === 'object' ? error.error : {};
  const message = normalizeMessage(body.message ?? error.message ?? '');
  const knownKeys = ['statusCode', 'errorCode', 'message', 'path', 'timestamp'];
  const extra: Record<string, unknown> = {};
  Object.keys(body).forEach((key) => {
    if (!knownKeys.includes(key)) {
      extra[key] = body[key];
    }
  });

  return {
    statusCode: error.status,
    statusText: error.statusText,
    errorCode: body.errorCode ?? undefined,
    message: message || (error.status === 0 ? 'Network error' : `HTTP ${error.status}`),
    path: body.path ?? error.url ?? undefined,
    timestamp: body.timestamp ?? undefined,
    extra: Object.keys(extra).length > 0 ? extra : undefined,
  };
}

/**
 * Intercepts HTTP errors and shows a modal with the full error details
 * (statusCode, errorCode, message, path, timestamp and any extra fields).
 * The error is still rethrown so subscribers can handle it (e.g. reset loading state).
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const modalService = inject(ModalService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      const payload = buildErrorPayload(error);
      modalService.open(ErrorModalComponent, {
        data: { payload },
        modalClass: 'app-modal-container modal-lg modal-dialog-centered',
        closeOnBackdropClick: true,
      });
      return throwError(() => error);
    })
  );
};
